import { motion } from "framer-motion";
import { Phone, Volume2, Loader2, Square } from "lucide-react";
import AdelaidaPortrait, { framesForVoice, MALE_FRAMES } from "@/components/AdelaidaPortrait";

// Top of Talk: his coach's face, always ready. Tap the face or the button to talk; "Hear" plays the greeting with lip-sync.
export default function CoachReady({ voice, compact, onTalk, onHear, hearState, amplitudeRef, speakingRef }) {
  const frames = framesForVoice(voice);
  const name = frames === MALE_FRAMES ? "Richard" : "Adelaida";
  const portrait = <AdelaidaPortrait frames={frames} amplitudeRef={amplitudeRef} speakingRef={speakingRef} status={hearState === "speaking" ? "speaking" : "idle"} />;
  if (compact) {
    return (
      <div className="flex items-center justify-between gap-3 mb-4" data-testid="coach-ready-compact">
        <button onClick={onTalk} className="flex items-center gap-3 text-left" aria-label={`Talk to ${name}`} data-testid="coach-ready-face-compact">
          <div className="relative w-12 h-12 rounded-full overflow-hidden border border-white/10 shrink-0">{portrait}</div>
          <div><div className="font-serif text-xl leading-tight">{name}</div><div className="text-xs text-muted-foreground">Your coach · tap to talk</div></div>
        </button>
        <button onClick={onTalk} data-testid="talk-call-btn" className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm hover:opacity-90 transition-opacity">
          <Phone className="w-4 h-4" /> Talk
        </button>
      </div>
    );
  }
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex flex-col items-center text-center mb-4" data-testid="coach-ready">
      <button onClick={onTalk} className="relative group" aria-label={`Talk to ${name}`} data-testid="coach-ready-portrait">
        <div className="absolute -inset-8 rounded-full blur-3xl opacity-50 pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.45), transparent 70%)" }} />
        <div className="relative overflow-hidden rounded-[2rem] border border-white/10 shadow-2xl transition-transform duration-500 group-hover:scale-[1.015]" style={{ width: "min(40vw, 175px)", aspectRatio: "3 / 4" }}>
          {portrait}
          <span className="absolute bottom-2 inset-x-0 flex justify-center"><span className="rounded-full bg-black/45 backdrop-blur-md px-3 py-1 text-[9px] uppercase tracking-[0.2em] text-white/85">Tap to talk</span></span>
        </div>
      </button>
      <p className="font-serif text-2xl sm:text-3xl mt-4" data-testid="coach-ready-name">{name} is here</p>
      <p className="text-sm text-muted-foreground mt-1">Talk out loud, or type below. Whatever's on your mind.</p>
      <div className="mt-4 flex items-center gap-3">
        <button onClick={onTalk} data-testid="talk-call-btn" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-3.5 text-base font-medium shadow-lg shadow-primary/20 hover:opacity-90 transition-opacity">
          <Phone className="w-5 h-5" /> Talk to {name}
        </button>
        {onHear && (
          <button onClick={onHear} disabled={hearState === "loading"} aria-label={`Hear ${name}'s greeting`} data-testid="coach-greeting-play"
            className="w-12 h-12 rounded-full border border-primary/40 text-primary flex items-center justify-center hover:bg-primary/10 transition-colors">
            {hearState === "loading" ? <Loader2 className="w-5 h-5 animate-spin" /> : hearState === "speaking" ? <Square className="w-4 h-4" /> : <Volume2 className="w-5 h-5" />}
          </button>
        )}
      </div>
    </motion.div>
  );
}
